<template>
  <section class="is-relative">
    <CustomDotAlert
      :display-alert="
        hasViewSalePricePermission &&
        (hasNewPriceAvailable || hasPriceLowerThanCost)
      "
      :tooltip="pricingTabTooltipText"
      :custom-style="`left: ${isAccommodationPackageService ? '58%' : '50%'}`"
    />
    <b-tabs
      :value="defaultOpenedTab"
      position="is-centered"
      class="block mb-0"
      @input="updateDefaultOpenedTab"
    >
      <EditServiceSideBarTabsInfo
        :is-edit-service-sidebar-open="open"
        :edit-service-sidebar-focused-element="editServiceSidebarFocusedElement"
        :form-itinerary-service="formItineraryService"
        :itinerary-service="serviceItinerary"
        :validate-function-execution="validateFunctionExecution"
        @update="emitUpdateForm"
      />
      <EditServiceSideBarTabsRoom
        :parent-service="parentService"
        :is-service-dates-range-edited="isServiceDatesRangeEdited"
        :form-itinerary-service="formItineraryService"
        :service-location="serviceLocation"
        :itinerary-service="serviceItinerary"
        :has-not-s-m-service-price="hasNotSMServicePrice"
        :set-loading="setLoading"
        :handle-automatic-price-calculate="handleAutomaticPriceCalculate"
        :handle-service-first-time="handleServiceFirstTime"
        @update="emitUpdateForm"
      />
      <EditServiceSideBarTabsPricing
        v-if="hasViewSalePricePermission"
        :is-service-dates-range-edited="isServiceDatesRangeEdited"
        :itinerary-service="serviceItinerary"
        :form-itinerary-service="formItineraryService"
        :original-service-price="originalServicePrice"
        :open="open"
        :has-not-s-m-service-price="hasNotSMServicePrice"
        :set-loading="setLoading"
        :automatic-price-calculate="automaticPriceCalculate"
        :handle-automatic-price-calculate="handleAutomaticPriceCalculate"
        :service-first-time="serviceFirstTime"
        :handle-service-first-time="handleServiceFirstTime"
        @update="emitUpdateForm"
      />
      <EditServiceSideBarTabsPaxs
        :parent-service="parentService"
        :itinerary="itinerary"
        :form-itinerary-service="formItineraryService"
        :passengers="passengers"
        :passenger-options="passengerOptions"
        @handled-toggle-switch="handledToggleSwitch"
        @update="emitUpdateForm"
      />
      <EditServiceSideBarTabsSubDetail
        v-if="hasSubservices"
        :parent-service="parentService"
        :itinerary="itinerary"
        :form-itinerary-service="formItineraryService"
      />
    </b-tabs>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import EditServiceSideBarTabsInfo from "@/components/_shared/EditServiceSideBarTabsInfo.vue";
import EditServiceSideBarTabsRoom from "@/components/_shared/EditServiceSideBarTabsRoom.vue";
import EditServiceSideBarTabsPricing from "@/components/_shared/EditServiceSideBarTabsPricing.vue";
import EditServiceSideBarTabsPaxs from "@/components/_shared/EditServiceSideBarTabsPaxs.vue";
import { serviceHelper } from "@/utils/service";
import { PERMISSION } from "@/constants";
import CustomDotAlert from "@/components/_shared/CustomDotAlert.vue";
import EditServiceSideBarTabsSubDetail from "@/components/_shared/EditServiceSideBarTabsSubDetail.vue";

export default {
  name: "EditServiceSideBarTabs",
  components: {
    EditServiceSideBarTabsSubDetail,
    EditServiceSideBarTabsInfo,
    EditServiceSideBarTabsRoom,
    EditServiceSideBarTabsPricing,
    EditServiceSideBarTabsPaxs,
    CustomDotAlert,
  },
  props: {
    isServiceDatesRangeEdited: {
      type: Boolean,
      default: false,
    },
    updateServiceViewState: {
      type: Function,
      default: () => {},
    },
    defaultOpenedTab: {
      type: Number,
      default: () => 1,
    },
    editServiceSidebarFocusedElement: {
      type: String,
      default: "",
    },
    formItineraryService: {
      type: Object,
      default: () => {},
    },
    serviceLocation: {
      type: Array,
      default: () => [],
    },
    serviceItinerary: {
      type: Object,
      default: () => {},
    },
    passengers: {
      type: [Array, Object],
      default: () => [],
    },
    open: {
      type: Boolean,
      default: () => false,
    },
    hasNotSMServicePrice: {
      type: Boolean,
      default: false,
    },
    parentService: {
      type: Object,
      default: () => {},
    },
    setLoading: {
      type: Function,
      default: () => {},
    },
    handleAutomaticPriceCalculate: {
      type: Function,
      default: () => {},
    },
    automaticPriceCalculate: {
      type: Boolean,
      default: true,
    },
    serviceFirstTime: {
      type: Boolean,
      default: false,
    },
    handleServiceFirstTime: {
      type: Function,
      default: () => {},
    },
    passengerOptions: {
      type: Array,
      default: () => [],
    },
    validateFunctionExecution: {
      type: Function,
      default: () => {},
    },
    originalServicePrice: {
      type: Number,
      default: 0,
    },
    itineraryCurrency: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      itinerary: "itinerary/getItinerary",
      authData: "auth/getAuthData",
    }),
    hasSubservices() {
      return (
        this.formItineraryService.is_composite_service &&
        this.parentService?.prices_costs_margins?.sub_prices_costs_margins
      );
    },
    hasViewSalePricePermission() {
      return serviceHelper.hasAnyViewPermissions(
        this.authData,
        this.itinerary,
        [PERMISSION.VIEW_SALE_PRICE]
      );
    },
    hasNewPriceAvailable() {
      return (
        this.originalServicePrice !== this.formItineraryService?.price &&
        ![null, 0].includes(this.originalServicePrice)
      );
    },
    hasPriceLowerThanCost() {
      const servicePriceCurrency =
        this.formItineraryService.service_price?.[0]?.currency_id ||
        this.itineraryCurrency?.id;

      const serviceCostCurrency =
        this.formItineraryService.service_supplier?.[0]?.currency_id ||
        this.itineraryCurrency?.id;

      const isDifferentCurrency = servicePriceCurrency !== serviceCostCurrency;

      if (isDifferentCurrency) return false;

      return (
        this.formItineraryService?.cost > 1 &&
        this.formItineraryService?.cost > this.formItineraryService?.price
      );
    },
    pricingTabTooltipText() {
      return this.hasPriceLowerThanCost
        ? "The price is lower than the cost"
        : this.hasNewPriceAvailable
        ? "Service price is outdated"
        : "";
    },
    isAccommodationPackageService() {
      return serviceHelper.isAccommodationPackageService(
        this.formItineraryService?.id_service_type
      );
    },
  },
  methods: {
    handledToggleSwitch: function (value) {
      this.$emit("handled-toggle-switch", value);
    },
    emitUpdateForm(value) {
      this.$emit("update", value);
    },
    updateDefaultOpenedTab(value) {
      this.updateServiceViewState("defaultOpenedTab", value);
    },
  },
};
</script>
